import { useState, useEffect, useCallback } from 'react';
import { championshipsApi, statsApi } from '../../lib/api';

export const useLeaderboard = () => {
  const [championships, setChampionships] = useState([]);
  const [selectedChampId, setSelectedChampId] = useState(null);
  const [standings, setStandings] = useState([]);
  const [topScorers, setTopScorers] = useState([]);
  const [weeklyScorers, setWeeklyScorers] = useState([]);
  const [topAssists, setTopAssists] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [activeSection, setActiveSection] = useState('standings');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await championshipsApi.list();
        const champs = (res.data || []).filter(
          (c) => c.status === 'in_progress' || c.status === 'completed'
        );
        setChampionships(champs);
        if (champs.length > 0) setSelectedChampId(champs[0].championship_id);
      } catch { /* silent */ } finally { setLoading(false); }
      try {
        const weekRes = await statsApi.topScorersWeek(5);
        setWeeklyScorers(weekRes.data || []);
      } catch { /* silent */ }
    })();
  }, []);

  const fetchChampData = useCallback(async () => {
    if (!selectedChampId) return;
    setLoadingData(true);
    try {
      const [standRes, scorerRes, assistRes, fixRes] = await Promise.all([
        championshipsApi.standings(selectedChampId),
        championshipsApi.topScorers(selectedChampId, 10),
        championshipsApi.topAssists(selectedChampId, 10),
        championshipsApi.getFixtures(selectedChampId),
      ]);
      setStandings(standRes.data || []);
      setTopScorers(scorerRes.data || []);
      setTopAssists(assistRes.data || []);
      setFixtures(fixRes.data || []);
    } catch { /* silent */ } finally { setLoadingData(false); }
  }, [selectedChampId]);

  useEffect(() => { fetchChampData(); }, [fetchChampData]);
  useEffect(() => {
    const interval = setInterval(fetchChampData, 60000);
    return () => clearInterval(interval);
  }, [fetchChampData]);

  const selectedChamp = championships.find((c) => c.championship_id === selectedChampId);

  const recentResults = fixtures
    .filter((f) => f.status === 'completed')
    .slice(-6)
    .reverse();

  const upcomingMatches = fixtures
    .filter((f) => f.status === 'scheduled' && f.match_date)
    .sort((a, b) => new Date(a.match_date) - new Date(b.match_date))
    .slice(0, 6);

  return {
    championships, selectedChampId, setSelectedChampId,
    standings, topScorers, topAssists, weeklyScorers,
    loading, loadingData, activeSection, setActiveSection,
    selectedChamp, recentResults, upcomingMatches,
    fetchChampData,
  };
};
